import { App, ConfigProvider } from 'antd';
import enUS from 'antd/es/locale/en_US';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useHirePortContext from '@/hooks/useHirePortContext';
import { isRtlLanguage } from '@/i18n/languages';
import { buildAntdTheme, SPACE_SM } from '@/theme/buildAntdTheme';
import { applyDocumentColorScheme } from '@/theme/colorScheme';
import { AntdAppBridge } from '@/utils/antdAppBridge';
import { setDayjsLocale } from '@/utils/dayjs';
import { getLanguageCode } from '@/utils/i18nLocale';

import type { Locale } from 'antd/es/locale';
import type React from 'react';

/** Dynamic loaders for non-English antd locale packs — each becomes a separate chunk.
 * Afrikaans (af) has no antd pack and falls back to enUS. */
const antdLocaleLoaders: Record<string, () => Promise<{ default: Locale }>> = {
  ar: () => import('antd/es/locale/ar_EG'),
  bg: () => import('antd/es/locale/bg_BG'),
  cs: () => import('antd/es/locale/cs_CZ'),
  da: () => import('antd/es/locale/da_DK'),
  de: () => import('antd/es/locale/de_DE'),
  el: () => import('antd/es/locale/el_GR'),
  es: () => import('antd/es/locale/es_ES'),
  et: () => import('antd/es/locale/et_EE'),
  fi: () => import('antd/es/locale/fi_FI'),
  fr: () => import('antd/es/locale/fr_FR'),
  he: () => import('antd/es/locale/he_IL'),
  hr: () => import('antd/es/locale/hr_HR'),
  hu: () => import('antd/es/locale/hu_HU'),
  id: () => import('antd/es/locale/id_ID'),
  it: () => import('antd/es/locale/it_IT'),
  ja: () => import('antd/es/locale/ja_JP'),
  ka: () => import('antd/es/locale/ka_GE'),
  ko: () => import('antd/es/locale/ko_KR'),
  lt: () => import('antd/es/locale/lt_LT'),
  lv: () => import('antd/es/locale/lv_LV'),
  nb: () => import('antd/es/locale/nb_NO'),
  nl: () => import('antd/es/locale/nl_NL'),
  pl: () => import('antd/es/locale/pl_PL'),
  pt: () => import('antd/es/locale/pt_PT'),
  ro: () => import('antd/es/locale/ro_RO'),
  ru: () => import('antd/es/locale/ru_RU'),
  sk: () => import('antd/es/locale/sk_SK'),
  sl: () => import('antd/es/locale/sl_SI'),
  sr: () => import('antd/es/locale/sr_RS'),
  sv: () => import('antd/es/locale/sv_SE'),
  th: () => import('antd/es/locale/th_TH'),
  tr: () => import('antd/es/locale/tr_TR'),
  uk: () => import('antd/es/locale/uk_UA'),
  vi: () => import('antd/es/locale/vi_VN'),
  zh: () => import('antd/es/locale/zh_CN'),
};

interface AntdConfigProviderProps {
  children: React.ReactNode;
}

/**
 * Single source of antd `ConfigProvider` config: brand theme (from HirePort
 * context) + locale + direction + component-level styles. Rendered by every
 * `HirePortContextProvider`, so the theme re-applies wherever the tenant brand
 * changes without scattering provider config across the app.
 */
const AntdConfigProvider: React.FC<AntdConfigProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const { theme, colorScheme } = useHirePortContext();
  const [currentLocale, setCurrentLocale] = useState<Locale>(enUS);

  const hirePortTheme = theme;
  const antdTheme = useMemo(
    () => buildAntdTheme(hirePortTheme, colorScheme),
    [hirePortTheme, colorScheme],
  );
  const lng = getLanguageCode(i18n.language);
  const direction = isRtlLanguage(lng) ? 'rtl' : 'ltr';

  useEffect(() => {
    setDayjsLocale(i18n.language);

    document.documentElement.lang = lng;
    document.documentElement.dir = direction;
    applyDocumentColorScheme(colorScheme);

    const loader = antdLocaleLoaders[lng];
    if (loader) {
      void loader().then((mod) => setCurrentLocale(mod.default));
    } else {
      void Promise.resolve(enUS).then(setCurrentLocale);
    }
  }, [i18n.language, lng, direction, colorScheme]);

  return (
    <ConfigProvider
      theme={antdTheme}
      locale={currentLocale}
      direction={direction}
      alert={{ styles: { root: { marginBottom: 16 } } }}
      badge={{
        styles: { indicator: { backgroundColor: hirePortTheme.colorPrimary } },
      }}
      drawer={{
        styles: {
          title: {
            fontSize: hirePortTheme.fontSizes.fontSize2xl,
          },
        },
        closable: {
          placement: 'end',
        },
      }}
      modal={{ centered: true }}
      empty={{
        styles: {
          image: {
            height: 'auto',
          },
        },
      }}
      calendar={{
        styles: {
          item: {
            verticalAlign: 'top',
          },
        },
      }}
      collapse={{
        styles: {
          header: {
            alignItems: 'center',
          },
        },
      }}
      menu={{
        styles: {
          root: { verticalAlign: 'middle' },
          item: { display: 'flex', alignItems: 'center' },
        },
      }}
      flex={{
        style: {
          gap: SPACE_SM,
        },
      }}
      upload={{
        styles: {
          root: {
            width: '100%',
          },
          trigger: {
            width: '100%',
          },
        },
      }}
      popover={{
        styles: {
          root: {
            maxHeight: 340,
            overflow: 'auto',
          },
        },
      }}
      steps={{
        styles: {
          itemContent: {
            marginTop: SPACE_SM,
          },
        },
      }}
      pagination={{
        styles: {
          root: {
            fontSize: hirePortTheme.fontSizes.fontSizeSm,
            color: hirePortTheme.colorMuted,
          },
        },
      }}
      tag={{
        styles: {
          root: {
            fontWeight: 500,
          },
        },
      }}
      table={{
        styles: {
          header: {
            cell: {
              padding: SPACE_SM,
            },
          },
        },
      }}
    >
      <App message={{ maxCount: 3 }}>
        <AntdAppBridge />
        {children}
      </App>
    </ConfigProvider>
  );
};

export default AntdConfigProvider;
